import React, { useEffect, useRef, useState } from "react";
import {
  Search, Filter, Layers, Star, Kanban, List, CalendarDays,
  Table2, BarChart3, Clock, Download, RefreshCw, ChevronLeft, ChevronRight,
  FileSpreadsheet, FileText, BriefcaseBusiness, Building2, ReceiptText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ODOO } from "./crmV2Helpers";

const viewBtn = (active) => ({
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: 3,
  background: active ? "#dee2e6" : "transparent",
  color: active ? ODOO.text : ODOO.muted,
  cursor: "pointer",
  padding: 0,
});

const dropdownBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: "none",
  background: "transparent",
  color: ODOO.text,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  padding: "4px 6px",
  fontFamily: "inherit",
};

const Dropdown = ({ label, icon: Icon, open, onToggle, children }) => (
  <div style={{ position: "relative", zIndex: open ? 50 : "auto" }}>
    <button type="button" onClick={onToggle} style={dropdownBtn}>
      {Icon && <Icon size={14} style={{ color: ODOO.muted }} />}
      {label}
      <span style={{ fontSize: 10, color: ODOO.muted }}>▾</span>
    </button>
    {open && (
      <>
        <div onClick={onToggle} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            minWidth: 200,
            maxHeight: "60vh",
            overflowY: "auto",
            background: "#fff",
            border: `1px solid ${ODOO.border}`,
            borderRadius: 4,
            boxShadow: "0 4px 16px rgba(0,0,0,.12)",
            padding: 6,
            marginTop: 2,
          }}
        >
          {children}
        </div>
      </>
    )}
  </div>
);

const menuItem = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,                      // espace uniforme entre icône et texte
  width: "100%",
  padding: "8px 12px",
  background: active ? "#f1f5f9" : "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 14,
  textAlign: "left",
  color: active ? "#0f172a" : "#334155",
  lineHeight: 1.2,             // évite les décalages verticaux
});

const CrmV2ControlPanel = ({
  view,
  onViewChange,
  search,
  onSearchChange,
  canManage,
  onNew,
  onGenerateLeads,
  onExport,
  onRefresh,
  filters,
  onFilterChange,
  stages = [],
  teams = [],
  users = [],
  listMeta,
  onListPageChange,
  onStubView,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = (key) => setOpenMenu((m) => (m === key ? null : key));

  const views = [
    { id: "kanban", icon: Kanban, real: true },
    { id: "list", icon: List, real: true },
    { id: "calendar", icon: CalendarDays, real: true },
    { id: "pivot", icon: Table2, real: true },
    { id: "graph", icon: BarChart3, real: true },
    { id: "activity", icon: Clock, real: true },
  ];

  const title =
    view === "calendar"
      ? `Pipeline (${new Date().toLocaleString("en-US", { month: "long", year: "numeric" })})`
      : "Pipeline";

  const showNewBtn = view === "kanban" || view === "list";

  return (
    <div
      style={{
        background: ODOO.controlBg,
        borderBottom: `1px solid ${ODOO.border}`,
        padding: "10px 16px 12px",
      }}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "0 0 auto", minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6 }}>
            <Dropdown
              label="CRM"
              icon={BriefcaseBusiness}
              open={openMenu === "crm"}
              onToggle={() => toggle("crm")}
            >
              <button
                type="button"
                onClick={() => { setOpenMenu(null); navigate("/admin-panel/crm"); }}
                style={menuItem(view !== "customers")}
              >
                <Kanban size={14} />
                <span>My Pipeline</span>
              </button>

              <button
                type="button"
                onClick={() => { setOpenMenu(null); navigate("/admin-panel/sales"); }}
                style={menuItem(false)}
              >
                <ReceiptText size={14} />
                <span>My Quotations</span>
              </button>

              <button
                type="button"
                onClick={() => { setOpenMenu(null); navigate("/admin-panel/crm/customers"); }}
                style={menuItem(view === "customers")}
              >
                <Building2 size={14} />
                <span>Customers</span>
              </button>
            </Dropdown>
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: ODOO.text, letterSpacing: "-0.02em" }}>
            {title}
          </h1>
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
            {canManage && showNewBtn && (
              <button
                type="button"
                onClick={onNew}
                style={{
                  background: ODOO.primary,
                  color: "#fff",
                  border: "none",
                  borderRadius: 3,
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                NEW
              </button>
            )}
            {canManage && showNewBtn && (
              <button
                type="button"
                onClick={onGenerateLeads}
                style={{
                  background: "#fff",
                  color: ODOO.primary,
                  border: `1px solid ${ODOO.primary}`,
                  borderRadius: 3,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                GENERATE LEADS
              </button>
            )}
            {onExport && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => toggle("export")}
                  title="Rapports — Excel / PDF"
                  style={{
                    background: openMenu === "export" ? ODOO.primarySoft : "transparent",
                    border: `1px solid ${openMenu === "export" ? ODOO.primary : ODOO.border}`,
                    borderRadius: 3,
                    height: 32,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    padding: "0 10px",
                    cursor: "pointer",
                    color: openMenu === "export" ? ODOO.primary : ODOO.muted,
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  <Download size={14} />
                  Rapports
                  <span style={{ fontSize: 9 }}>▾</span>
                </button>
                {openMenu === "export" && (
                  <>
                    <div onClick={() => setOpenMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        zIndex: 1000,
                        background: "#fff",
                        border: `1px solid ${ODOO.border}`,
                        borderRadius: 4,
                        boxShadow: "0 4px 16px rgba(0,0,0,.12)",
                        marginTop: 4,
                        minWidth: 220,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => { setOpenMenu(null); onExport("xlsx"); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", border: "none", background: "#fff", padding: "10px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = ODOO.primarySoft)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <FileSpreadsheet size={16} style={{ color: "#107c41" }} />
                        <span>
                          <span style={{ display: "block", fontWeight: 600, color: ODOO.text }}>Excel (.xlsx)</span>
                          <span style={{ display: "block", fontSize: 11, color: ODOO.muted }}>Pipeline + stats, lisible & filtré</span>
                        </span>
                      </button>
                      <div style={{ height: 1, background: ODOO.borderLight }} />
                      <button
                        type="button"
                        onClick={() => { setOpenMenu(null); onExport("pdf"); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", border: "none", background: "#fff", padding: "10px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = ODOO.primarySoft)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <FileText size={16} style={{ color: ODOO.primary }} />
                        <span>
                          <span style={{ display: "block", fontWeight: 600, color: ODOO.text }}>PDF (rapport)</span>
                          <span style={{ display: "block", fontSize: 11, color: ODOO.muted }}>Pipeline + stats, lisible & filtré</span>
                        </span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: "1 1 360px", minWidth: 260 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${ODOO.border}`,
              borderRadius: 3,
              background: "#fff",
              padding: "0 10px",
              height: 34,
            }}
          >
            <Search size={15} style={{ color: ODOO.muted, flexShrink: 0 }} />
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search..."
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: 13.5,
                padding: "6px 8px",
                background: "transparent",
                fontFamily: "inherit",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginTop: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Dropdown label="Filters" icon={Filter} open={openMenu === "filters"} onToggle={() => toggle("filters")}>
              <div style={{ fontSize: 11, fontWeight: 700, color: ODOO.muted, padding: "4px 10px 6px", textTransform: "uppercase" }}>Type</div>
              {[
                { v: "all", l: "All types" },
                { v: "lead", l: "Leads" },
                { v: "opportunity", l: "Opportunities" },
              ].map((o) => (
                <button key={o.v} type="button" style={menuItem(filters.type === o.v)} onClick={() => { onFilterChange({ type: o.v }); setOpenMenu(null); }}>
                  {o.l}
                </button>
              ))}
              <div style={{ height: 1, background: ODOO.borderLight, margin: "6px 0" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: ODOO.muted, padding: "4px 10px 6px", textTransform: "uppercase" }}>Stage</div>
              <button type="button" style={menuItem(filters.stage_id === "all")} onClick={() => { onFilterChange({ stage_id: "all" }); setOpenMenu(null); }}>
                All stages
              </button>
              {stages.map((s) => (
                <button key={s.id} type="button" style={menuItem(String(filters.stage_id) === String(s.id))} onClick={() => { onFilterChange({ stage_id: s.id }); setOpenMenu(null); }}>
                  {s.name}
                </button>
              ))}
              <div style={{ height: 1, background: ODOO.borderLight, margin: "6px 0" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: ODOO.muted, padding: "4px 10px 6px", textTransform: "uppercase" }}>Team</div>
              <button type="button" style={menuItem(filters.team_id === "all")} onClick={() => { onFilterChange({ team_id: "all" }); setOpenMenu(null); }}>
                All teams
              </button>
              {teams.map((t) => (
                <button key={t.id} type="button" style={menuItem(String(filters.team_id) === String(t.id))} onClick={() => { onFilterChange({ team_id: t.id }); setOpenMenu(null); }}>
                  {t.name}
                </button>
              ))}
              <div style={{ height: 1, background: ODOO.borderLight, margin: "6px 0" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: ODOO.muted, padding: "4px 10px 6px", textTransform: "uppercase" }}>Salesperson</div>
              <button type="button" style={menuItem(filters.user_id === "all")} onClick={() => { onFilterChange({ user_id: "all" }); setOpenMenu(null); }}>
                All salespeople
              </button>
              {users.slice(0, 40).map((u) => (
                <button key={u.id} type="button" style={menuItem(String(filters.user_id) === String(u.id))} onClick={() => { onFilterChange({ user_id: u.id }); setOpenMenu(null); }}>
                  {u.name || u.username || u.email || u.login}
                </button>
              ))}
              <div style={{ height: 1, background: ODOO.borderLight, margin: "6px 0" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: ODOO.muted, padding: "4px 10px 6px", textTransform: "uppercase" }}>Priority</div>
              {[
                { v: "all", l: "All priorities" },
                { v: "0", l: "☆ Low" },
                { v: "1", l: "★ Medium" },
                { v: "2", l: "★★ High" },
                { v: "3", l: "★★★ Urgent" },
              ].map((o) => (
                <button key={o.v} type="button" style={menuItem(String(filters.priority) === String(o.v))} onClick={() => { onFilterChange({ priority: o.v }); setOpenMenu(null); }}>
                  {o.l}
                </button>
              ))}
              <div style={{ height: 1, background: ODOO.borderLight, margin: "6px 0" }} />
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={!!filters.include_inactive} onChange={(e) => { onFilterChange({ include_inactive: e.target.checked }); setOpenMenu(null); }} />
                Show archived
              </label>
              {(filters.type !== "all" || filters.stage_id !== "all" || filters.team_id !== "all" || filters.user_id !== "all" || filters.priority !== "all" || filters.include_inactive) && (
                <button type="button" style={{ ...menuItem(false), marginTop: 6, justifyContent: "center", borderTop: `1px solid ${ODOO.borderLight}`, color: "#be123c", fontWeight: 600 }} onClick={() => { onFilterChange({ type: "all", stage_id: "all", team_id: "all", user_id: "all", priority: "all", include_inactive: false }); setOpenMenu(null); }}>
                  Clear filters
                </button>
              )}
            </Dropdown>

            <Dropdown label="Group By" icon={Layers} open={openMenu === "groupby"} onToggle={() => toggle("groupby")}>
              <button type="button" style={menuItem(false)} onClick={() => setOpenMenu(null)}>Stage</button>
              <button type="button" style={menuItem(false)} onClick={() => setOpenMenu(null)}>Salesperson</button>
              <button type="button" style={menuItem(false)} onClick={() => setOpenMenu(null)}>Team</button>
              <div style={{ fontSize: 11, color: ODOO.muted, padding: "8px 10px" }}>Group By — bientôt</div>
            </Dropdown>

            <Dropdown label="Favorites" icon={Star} open={openMenu === "fav"} onToggle={() => toggle("fav")}>
              <div style={{ fontSize: 12, color: ODOO.muted, padding: "8px 10px" }}>Aucun favori enregistré</div>
            </Dropdown>

            {view === "list" && listMeta && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, fontSize: 13, color: ODOO.muted }}>
                <button type="button" onClick={onRefresh} title="Refresh" style={{ ...viewBtn(false), width: 24, height: 24 }}>
                  <RefreshCw size={13} />
                </button>
                <span>
                  {listMeta.from}-{listMeta.to} / {listMeta.total}
                </span>
                <button
                  type="button"
                  disabled={listMeta.page <= 1}
                  onClick={() => onListPageChange(listMeta.page - 1)}
                  style={{ ...viewBtn(false), opacity: listMeta.page <= 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  disabled={listMeta.to >= listMeta.total}
                  onClick={() => onListPageChange(listMeta.page + 1)}
                  style={{ ...viewBtn(false), opacity: listMeta.to >= listMeta.total ? 0.4 : 1 }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}

            <div style={{ display: "inline-flex", alignItems: "center", gap: 1, marginLeft: 8, borderLeft: `1px solid ${ODOO.borderLight}`, paddingLeft: 8 }}>
              {views.map((v) => {
                const Icon = v.icon;
                return (
                  <button
                    key={v.id}
                    type="button"
                    title={v.id}
                    onClick={() => (v.real ? onViewChange(v.id) : onStubView?.(v.id))}
                    style={viewBtn(view === v.id)}
                  >
                    <Icon size={15} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmV2ControlPanel;